import { createContext, useContext, useReducer } from 'react';
import type { ReactElement, ReactNode } from 'react';

import type { ReducedCartItem } from 'models/CartItem';
import Cart from 'models/Cart';
import Alert from 'models/Alert';

type Dispatch = (action: Action) => void;

type Action =
  | { type: 'toggleSummaryVisible'; isSummaryVisible: boolean }
  | { type: 'setMenuItemQuantity'; skuId: number; quantity: number }
  | { type: 'setMenuItemQuantities'; skuIds: ReducedCartItem[]; setMaxQty: boolean }
  | { type: 'setAlert'; alert: Alert }
  | { type: 'setAddedOutsideWidget'; skuId: number;}
  | { type: 'setRemovedOutsideWidget'; skuId: number;};

type Props = {
  children: ReactNode;
  value: Cart;
};

type ContextValue = {
  cart: Cart;
  dispatch: Dispatch;
};

const CartContext = createContext<ContextValue | null>(null);

function cartReducer(cart: Cart, action: Action): Cart {
  switch (action.type) {
    case 'setAddedOutsideWidget': {
      const { skus_added_outside_widget: skusAddedOutsideWidget } = cart;
      // @ts-expect-error TODO: fix null safety
      if (!skusAddedOutsideWidget.map((item) => item.sku.id).includes(action.skuId)) {
        const packageProduct = cart.menu_items.find((item) => item.sku.id === action.skuId);
        // @ts-expect-error TODO: fix null safety
        skusAddedOutsideWidget.push(packageProduct);
      }
      const packageProductMenuItems = cart.menu_items.filter((item) => {
        const hasMatchingSku = cart.package_products.some(
          (packageProduct) => packageProduct.sku_id === item.sku.id,
        );

        return hasMatchingSku;
      });
      return new Cart({
        ...cart,
        skus_added_outside_widget: skusAddedOutsideWidget,
        last_added_by_recipe_tile: packageProductMenuItems
          .filter((item) => item.quantity > 0).length === cart.package_products.length,
      });
    }

    case 'setRemovedOutsideWidget': {
      const { skus_added_outside_widget: skusAddedOutsideWidget } = cart;
      // @ts-expect-error TODO: fix null safety
      const index = skusAddedOutsideWidget.findIndex((item) => item.sku.id === action.skuId);
      if (index !== -1) {
        skusAddedOutsideWidget.splice(index, 1);
      }
      return new Cart({
        ...cart,
        skus_added_outside_widget: skusAddedOutsideWidget,
        last_added_by_recipe_tile: false,
      });
    }

    case 'toggleSummaryVisible': {
      return new Cart({
        ...cart,
        isSummaryVisible: action.isSummaryVisible,
      });
    }

    case 'setMenuItemQuantity': {
      const menuItem = cart.getCartItem(action.skuId);
      menuItem.quantity = action.quantity;

      const updatedCart = new Cart({
        ...cart,
        menu_items: [...cart.menu_items],
      });

      updatedCart.validate();

      return updatedCart;
    }

    case 'setMenuItemQuantities': {
      action.skuIds.forEach((item) => {
        const menuItem = cart.getCartItem(item.skuId);

        if (menuItem.sku.id === item.skuId) {
          menuItem.quantity = item.quantity;

          if (action.setMaxQty) {
            menuItem.max_quantity = item.quantity;
          }
        }
      });

      const updatedCart = new Cart({
        ...cart,
        menu_items: [...cart.menu_items],
      });

      updatedCart.validate();

      return updatedCart;
    }

    case 'setAlert': {
      return new Cart({ ...cart, alert: action.alert });
    }

    default: {
      return cart;
    }
  }
}

function CartProvider({ children, value }: Props): ReactElement {
  const [cart, dispatch] = useReducer(
    cartReducer,
    value,
  );

  return (
    <CartContext.Provider value={{ cart, dispatch }}>
      {children}
    </CartContext.Provider>
  );
}

function useCart(): ContextValue {
  const context = useContext(CartContext);

  if (context === null) {
    throw new Error('useCart must be used within a CartProvider');
  }

  return context;
}

export { useCart, CartProvider };
